import { useCallback, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useWorkspace } from "../../app/workspace";
import { Banner } from "../../components/ui/Banner";
import { Button, ButtonLink } from "../../components/ui/Button";
import { Card, CardHeader } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/Dialog";
import { ErrorDetail } from "../../components/ui/ErrorDetail";
import { ErrorState } from "../../components/ui/ErrorState";
import { Loading, PageHeader } from "../../components/ui/Page";
import { useAction } from "../../hooks/useAction";
import { useNow } from "../../hooks/useNow";
import { usePoll } from "../../hooks/usePoll";
import { useResource } from "../../hooks/useResource";
import { streamsApi } from "../../lib/api/streams";
import type { Stream } from "../../lib/api/types";
import { formatDateTime, formatDuration } from "../../lib/format";
import { errorDetailText, exitReasonText, isEditable, PLATFORM_LABEL, primaryAction, uptimeMs } from "../../lib/stream-status";
import { EventsCard, RunsCard } from "./History";
import { OnAirBoard } from "./OnAirBoard";
import { StatusPill } from "./StatusPill";
import { useStreamControl } from "./useStreamControl";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3 border-b border-line px-5 py-2.5 last:border-b-0 sm:grid-cols-[10rem_1fr]">
      <dt className="font-mono text-xs leading-5 text-muted">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function StatusCard({ s, now }: { s: Stream; now: number }) {
  const up = uptimeMs(s, now);
  const reason = exitReasonText(s.lastExitReason, s.platform);
  const detail = errorDetailText(s.lastError);
  return (
    <Card raised>
      <CardHeader title="Status" />
      {up !== null && <OnAirBoard s={s} />}
      <dl>
        <Row label="state"><StatusPill stream={s} /></Row>
        <Row label="since">{formatDateTime(s.stateChangedAt)}</Row>
        {s.actualState === "RETRYING" && s.nextAttemptAt && <Row label="next try">{formatDateTime(s.nextAttemptAt)} (attempt {s.attempt + 1})</Row>}
        {reason && s.actualState !== "RUNNING" && <Row label="last stop">{reason}</Row>}
        {detail && s.actualState !== "RUNNING" && <Row label="details"><ErrorDetail text={detail} /></Row>}
      </dl>
    </Card>
  );
}

function SettingsCard({ s }: { s: Stream }) {
  return (
    <Card>
      <CardHeader title="Settings" />
      <dl>
        <Row label="platform">{PLATFORM_LABEL[s.platform]}</Row>
        <Row label="rtmp url"><code>{s.rtmpUrl}</code></Row>
        <Row label="stream key"><code>••••{s.streamKeyLast4}</code></Row>
        <Row label="output">{s.quality === "4k" ? "4K" : "HD"} · {s.fps} fps · {s.orientation}</Row>
        <Row label="playback">{s.loopMode === "loop" ? "loop the playlist" : "play once, then stop"}</Row>
        <Row label="starts">{s.scheduledStartAt ? formatDateTime(s.scheduledStartAt) : "when you press start"}</Row>
        <Row label="ends">{s.scheduledEndAt ? formatDateTime(s.scheduledEndAt) : "when you press stop"}</Row>
      </dl>
    </Card>
  );
}

function PlaylistCard({ s }: { s: Stream }) {
  const items = s.playlist ?? [];
  return (
    <Card>
      <CardHeader
        title="Playlist"
        caption={s.estimatedPlaylistMs ? `${formatDuration(s.estimatedPlaylistMs)} in total` : "length known once every video is processed"}
      />
      <ol className="flex flex-col">
        {items.map((p) => (
          <li key={p.position} className="flex justify-between gap-3 border-b border-line px-5 py-2.5 last:border-b-0">
            <span><span className="font-mono text-muted">{String(p.position + 1).padStart(2, "0")}</span> {p.name}</span>
            <span className="font-mono text-muted tabular-nums">{formatDuration(p.durationMs)}</span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

export function StreamDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { can, writable } = useWorkspace();
  const stream = useResource((signal) => streamsApi.get(id, signal), [id]);
  const now = useNow(30_000);
  usePoll(() => void stream.reload(), 5_000, stream.error === undefined);

  const onChanged = useCallback((s: Stream) => stream.set(s), [stream]);
  const control = useStreamControl(onChanged);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useAction(async () => { await streamsApi.remove(id); navigate("/streams", { replace: true }); });

  if (stream.error !== undefined && !stream.data) return <ErrorState error={stream.error} onRetry={() => void stream.reload()} />;
  if (!stream.data) return <Loading />;
  const s = stream.data;
  const action = primaryAction(s);
  const editable = isEditable(s) && can("stream.write") && writable;
  // Runs and events refetch whenever the state changes, not on every poll.
  const version = `${s.actualState}:${s.stateChangedAt}`;

  return (
    <>
      <PageHeader
        caption={<Link to="/streams">streams</Link>}
        title={s.name}
        actions={<>
          {editable && <ButtonLink to={`/streams/${s.id}/edit`}>edit</ButtonLink>}
          {editable && <Button variant="danger" onClick={() => setConfirmDelete(true)}>delete</Button>}
          {can("stream.control") && (
            <Button variant={action === "start" ? "primary" : "secondary"} pending={control.busy === s.id} onClick={() => void control.toggle(s)}>
              {action === "start" ? "start stream →" : "stop stream"}
            </Button>
          )}
        </>}
      />
      {control.errors[s.id] && <Banner tone="bad">{control.errors[s.id]}</Banner>}
      {!isEditable(s) && can("stream.write") && <p className="-mt-4 font-mono text-xs text-muted">// stop the stream to change its settings or playlist.</p>}

      <div className="grid gap-6 lg:grid-cols-2">
        <StatusCard s={s} now={now} />
        <SettingsCard s={s} />
      </div>
      <PlaylistCard s={s} />
      <RunsCard streamId={s.id} platform={s.platform} version={version} />
      <EventsCard streamId={s.id} version={version} />

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${s.name}?`}
        confirmLabel="delete stream"
        danger
        pending={remove.pending}
        onConfirm={() => void remove.run()}
        onCancel={() => setConfirmDelete(false)}
      >
        <p>The stream's settings and history go away. Your videos stay in the library.</p>
        {remove.error && <Banner tone="bad">{remove.error}</Banner>}
      </ConfirmDialog>
    </>
  );
}
